import { message } from 'antd';
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWith409 from './baseQueryWith409';
import { env } from 'configs/EnvironmentConfig';
import qs from 'qs';
import fetch from 'auth/FetchInterceptor';

export const getUsuarios = query =>
	fetch
		.get(
			`/users${
				query
					? `?${qs.stringify(
							{ ...query, publicationState: 'live' },
							{
								encodeValuesOnly: true,
							}
					  )}`
					: ''
			}`
		)
		.then(response => ({
			data: response.map(item => {
				let id = item.id;
				delete item.id;
				return { id, attributes: { ...item } };
			}),
			meta: {
				pagination: {
					total: response.length,
				},
			},
		}));
export const getUsuario = ({ id, query }) =>
	fetch
		.get(
			`/users/${id}${
				query
					? `?${qs.stringify(
							{ ...query, publicationState: 'live' },
							{
								encodeValuesOnly: true,
							}
					  )}`
					: ''
			}`
		)
		.then(response => {
			let id = response.id;
			delete response.id;

			return {
				data: { id, attributes: { ...response } },
				meta: {},
			};
		});
export const getMe = query =>
	fetch
		.get(
			`/users/me${
				query
					? `?${qs.stringify(
							{ ...query, publicationState: 'live' },
							{
								encodeValuesOnly: true,
							}
					  )}`
					: ''
			}`
		)
		.then(response => ({
			data: response,
			meta: {},
		}));
export const addUsuario = data => fetch.post('/users', data);
export const updateUsuario = ({ id, ...data }) =>
	fetch.put(`/users/${id}`, data);
export const deleteUsuario = id => fetch.delete(`/users/${id}`);

// RTK Query
export const usuariosApi = createApi({
	reducerPath: 'usuariosApi',
	baseQuery: baseQueryWith409,

	// default configuration
	refetchOnReconnect: true,
	refetchOnMountOrArgChange: true,
	refetchOnFocus: true,

	//endpoints
	endpoints: build => ({
		getUsuarios: build.query({
			query: query => {
				return {
					url: `/users${
						query
							? `?${qs.stringify(
									{ ...query, publicationState: 'live' },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
			providesTags: result =>
				// is result available?
				result
					? // successful query
					  [
							...result.data.map(({ id }) => ({ type: 'Usuarios', id })),
							{ type: 'Usuarios', id: 'LIST' },
					  ]
					: // an error occurred, but we still want to refetch this query when `{ type: 'Posts', id: 'LIST' }` is invalidated
					  [{ type: 'Usuarios', id: 'LIST' }],
			transformResponse: response => ({
				data: response.map(item => {
					let id = item.id;
					delete item.id;
					return { id, attributes: { ...item } };
				}),
				meta: {
					pagination: {
						total: response.length,
					},
				},
			}),
		}),
		getUsuario: build.query({
			query: ({ id, query }) => {
				return {
					url: `/users/${id}${
						query
							? `?${qs.stringify(
									{ ...query, publicationState: 'live' },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
			transformResponse: response => {
				let id = response.id;
				delete response.id;

				return { id, attributes: { ...response } };
			},
			providesTags: (result, error, id) => [{ type: 'Usuarios', id }],
		}),
		getMe: build.query({
			query: query => {
				const defaultQuery = {
					populate: ['role', 'avatar'],
					...query,
				};

				return {
					url: `/users/me${
						defaultQuery
							? `?${qs.stringify(
									{ ...defaultQuery, publicationState: 'live' },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
			providesTags: ['Me'],
		}),

		// Nuevo endpoint específico para refrescar datos del usuario
		refreshMe: build.query({
			query: () => ({
				url: `/users/me?${qs.stringify(
					{
						populate: ['role', 'avatar'],
						publicationState: 'live',
					},
					{ encodeValuesOnly: true }
				)}`,
				method: 'GET',
			}),
			providesTags: ['Me'],
			keepUnusedDataFor: 0, // No cache, siempre fresh
		}),
		addUsuario: build.mutation({
			query: data => {
				const user = JSON.parse(localStorage.getItem('me'));
				return {
					url: `/users`,
					method: 'POST',
					body: JSON.stringify({
						...data,
						password: env.DEFAULT_USERS_PASSWORD,
						created_by_user: user.id,
					}),
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
					message.success('Usuario agregado exitosamente');
				} catch (error) {
					message.error('Error al agregar usuario');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: [{ type: 'Usuarios', id: 'LIST' }],
		}),
		updateUsuario: build.mutation({
			query: ({ id, ...data }) => {
				const user = JSON.parse(localStorage.getItem('me'));
				return {
					url: `/users/${id}`,
					method: 'PUT',
					body: JSON.stringify({
						...data,
						updated_by_user: user.id,
					}),
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
					message.success('Usuario actualizado exitosamente');
				} catch (error) {
					message.error('Error al actualizar usuario');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: (result, error, { id }) => [{ type: 'Usuarios', id }],
		}),
		updateMe: build.mutation({
			query: data => {
				const user = JSON.parse(localStorage.getItem('me'));
				return {
					url: `/users-permissions/users-custom/me`,
					method: 'PUT',
					body: JSON.stringify({
						...data,
						updated_by_user: user.id,
					}),
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
					message.success('Perfil actualizado exitosamente');
				} catch (error) {
					message.error('Error al actualizar perfil');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: ['Me'],
		}),
		deleteUsuario: build.mutation({
			query: id => {
				const user = JSON.parse(localStorage.getItem('me'));
				return {
					url: `/users/${id}`,
					method: 'PUT',
					body: JSON.stringify({
						data: {
							publishedAt: null,
							updated_by_user: user.id,
						},
					}),
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
					message.success('Usuario eliminado exitosamente');
				} catch (error) {
					message.error('Error al eliminar usuario');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: (result, error, id) => [{ type: 'Usuarios', id }],
		}),
		deleteAccount: build.mutation({
			query: () => ({
				url: `/users-permissions/auth/delete-account/me`,
				method: 'DELETE',
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					message.success('Cuenta eliminada exitosamente');
				} catch (error) {
					message.error('Error al eliminar la cuenta');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
		}),
		deleteUsuarioPermanente: build.mutation({
			query: id => ({
				url: `/users-permissions/auth/delete-account/${id}`,
				method: 'DELETE',
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					message.success('Usuario dado de baja exitosamente');
				} catch (error) {
					message.error('Error al dar de baja usuario');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: [{ type: 'Usuarios', id: 'LIST' }],
		}),
	}),
});

export const {
	useGetUsuariosQuery,
	useGetUsuarioQuery,
	useGetMeQuery,
	useRefreshMeQuery,
	useLazyRefreshMeQuery,
	useAddUsuarioMutation,
	useUpdateUsuarioMutation,
	useUpdateMeMutation,
	useDeleteUsuarioMutation,
	useDeleteAccountMutation,
	useDeleteUsuarioPermanenteMutation,
} = usuariosApi;
